import React, { useState, useEffect, useCallback } from 'react';
import { API_URL, US_STATES, S, maskDOB, maskPhone, Fld, Inp, Sel, DocDone } from './ui';
import ScrTask from './ScrTask';

function taskTypeLabel(type) {
  if (type === 'pdf_generate') return 'Download & print';
  if (type === 'pdf_sign')     return 'Sign electronically';
  if (type === 'upload')       return 'Upload file';
  return '';
}
function taskIcon(status, type) {
  if (status === 'completed') return '✓';
  if (type === 'pdf_generate') return '📄';
  if (type === 'pdf_sign')     return '✍️';
  if (type === 'upload')       return '📎';
  return '○';
}

// ── Screen: Email / Magic Link request ────────────────────────────────────────

function EmailScreen({ onSent }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit() {
    if (!email.trim() || !email.includes('@')) { setError('Enter a valid email address'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/onboarding/request-link`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || 'Something went wrong. Please try again.');
        return;
      }
      setSent(true);
    } catch(e) {
      setError('Something went wrong. Please try again.');
    } finally { setLoading(false); }
  }

  return (
    <div style={S.page}>
      <div style={{ textAlign:'center', marginBottom:'2rem', marginTop:'3rem' }}>
        <div style={{ fontSize:32, marginBottom:12 }}>📋</div>
        <div style={S.h1}>Staff Onboarding</div>
        <div style={S.sub}>Little Scholars NYC</div>
      </div>
      <div style={S.card}>
        {sent ? (
          <div style={{ textAlign:'center', padding:'1rem 0' }}>
            <div style={{ fontSize:28, marginBottom:12 }}>📬</div>
            <div style={{ fontSize:15, fontWeight:500, color:'#1a1a1a', marginBottom:8 }}>Check your inbox</div>
            <div style={{ fontSize:13, color:'#6b7280', marginBottom:'1.5rem' }}>
              We sent a secure link to <strong>{email}</strong>.<br/>
              The link expires in 15 minutes.
            </div>
            <button style={{ ...S.btnBack, fontSize:13 }} onClick={() => setSent(false)}>
              Use a different email
            </button>
          </div>
        ) : (
          <>
            <div style={{ ...S.h2, marginBottom:6 }}>Sign in to continue</div>
            <div style={{ fontSize:13, color:'#6b7280', marginBottom:'1.25rem' }}>
              Enter the email address your employer used for your offer.
              We'll send you a secure sign-in link.
            </div>
            <Fld label="Email address" error={error}>
              <Inp value={email} onChange={setEmail} error={!!error}
                placeholder="jane@example.com" type="email"
                onKeyDown={e => e.key==='Enter' && handleSubmit()} />
            </Fld>
            <button style={S.btnPrimary} onClick={handleSubmit} disabled={loading}>
              {loading ? 'Sending...' : 'Send me a link →'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Screen: Dashboard ─────────────────────────────────────────────────────────

function Dashboard({ session, token, onSelectTask }) {
  const { first_name, last_name, position, start_date, location, progress, tasks } = session;
  const pct = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;

  return (
    <div style={S.page}>
      <div style={{ marginBottom:'1.5rem' }}>
        <div style={S.h1}>Welcome, {first_name}! 👋</div>
        <div style={S.sub}>{position} · {location?.display_name} · Starting {start_date}</div>
      </div>

      <div style={S.card}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:13, fontWeight:500, color:'#374151' }}>Onboarding progress</div>
            <div style={{ fontSize:13, color:'#6b7280', marginTop:2 }}>{progress.completed} of {progress.total} tasks completed</div>
          </div>
          <div style={{ fontSize:24, fontWeight:600, color: pct===100?'#16a34a':'#2563eb' }}>{pct}%</div>
        </div>
        <div style={S.progress}>
          <div style={S.progressBar(pct)} />
        </div>
      </div>

      <div style={{ marginBottom:'.75rem' }}>
        <div style={S.sec}>Your tasks</div>
        {tasks.map(task => {
          const isStub = task.stub;
          const status = task.status;
          return (
            <div key={task.id}
              style={S.taskCard(isStub ? 'stub' : status)}
              onClick={() => !isStub && onSelectTask(task)}>
              <div style={S.taskIcon(status, task.type)}>
                {taskIcon(status, task.type)}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:16, fontWeight:500, color:'#1a1a1a' }}>{task.title}</div>
                <div style={{ fontSize:13, color:'#6b7280', marginTop:4 }}>{taskTypeLabel(task.type)}</div>
              </div>
              <div style={S.badge(isStub ? 'stub' : status)}>
                {isStub ? 'Coming soon' : status === 'completed' ? 'Completed' : status === 'in_progress' ? 'In progress' : 'To do'}
              </div>
            </div>
          );
        })}
      </div>

      {pct === 100 && (
        <div style={{ ...S.card, background:'#f0fdf4', border:'1px solid #bbf7d0', textAlign:'center' }}>
          <div style={{ fontSize:24, marginBottom:8 }}>🎉</div>
          <div style={{ fontSize:15, fontWeight:600, color:'#15803d' }}>All tasks completed!</div>
          <div style={{ fontSize:13, color:'#16a34a', marginTop:4 }}>Your onboarding documents are ready. See you on {start_date}!</div>
        </div>
      )}
    </div>
  );
}

// ── Task: Upload ──────────────────────────────────────────────────────────────

function UploadTask({ task, token, onBack, onComplete }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleUpload() {
    if (files.length === 0) { setError('Please select at least one file'); return; }
    setLoading(true); setError('');
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('token', token);
        fd.append('task_id', task.id);
        fd.append('file', file);
        const res = await fetch(`${API_URL}/api/onboarding/upload`, { method:'POST', body:fd });
        if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      }
      onComplete(task.id);
    } catch(e) {
      setError(e.message || 'Upload failed. Please try again.');
    } finally { setLoading(false); }
  }

  return (
    <div style={S.page}>
      <button style={{ ...S.btnBack, marginBottom:'1.25rem' }} onClick={onBack}>← Back</button>
      <div style={{ marginBottom:'1.25rem' }}>
        <div style={S.h1}>{task.title}</div>
        <div style={S.sub}>{task.description}</div>
      </div>
      <div style={S.card}>
        <div style={S.uploadArea} onClick={() => document.getElementById('file-input').click()}>
          <div style={{ fontSize:28, marginBottom:8 }}>📎</div>
          <div style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Click to select files</div>
          <div style={{ fontSize:12, color:'#9ca3af', marginTop:4 }}>PDF, JPG, PNG accepted</div>
          <input id="file-input" type="file" multiple={task.multiple}
            accept={task.accept || 'image/*,application/pdf'} style={{ display:'none' }}
            onChange={e => setFiles(Array.from(e.target.files))} />
        </div>
        {files.length > 0 && (
          <div style={{ marginTop:'1rem' }}>
            {files.map((f,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#374151', padding:'6px 0', borderBottom:'1px solid #f3f4f6' }}>
                <span>📄</span><span style={{ flex:1 }}>{f.name}</span>
                <span style={{ color:'#9ca3af' }}>{(f.size/1024).toFixed(0)} KB</span>
              </div>
            ))}
          </div>
        )}
        {error && <div style={{ ...S.err, marginTop:'.75rem' }}>{error}</div>}
        <button style={{ ...S.btnPrimary, marginTop:'1.25rem' }} onClick={handleUpload} disabled={loading || files.length===0}>
          {loading ? 'Uploading...' : `Upload ${files.length > 1 ? files.length+' files' : 'file'}`}
        </button>
      </div>
    </div>
  );
}

// ── Task: CBC Worksheet ───────────────────────────────────────────────────────

const CBC_STEPS = ['Personal','Contact','Address','History'];

function CBCTask({ task, token, session, onBack, onComplete }) {
  const [step, setStep] = useState(0);
  const [d, setDState] = useState({
    first_name: session.first_name || '',
    middle_name:'', last_name: session.last_name || '', dob:'',
    ssn1:'', ssn2:'', ssn3:'', no_ssn:false, alien_reg:'',
    email:'', phone:'',
    home_address1:'', home_apt:'', home_no_apt:false, home_city:'', home_state:'', home_zip:'',
    same_mailing:true,
    mailing_address1:'', mailing_apt:'', mailing_no_apt:false, mailing_city:'', mailing_state:'', mailing_zip:'',
    other_names_yes:null, other_names_list:'',
    lived_outside_ny:null, prev_addresses:[],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [done, setDone] = useState(false);

  const setD = useCallback((key, val) => {
    setDState(prev => ({ ...prev, [key]: typeof val==='function'?val(prev[key]):val }));
    setErrors(prev => ({ ...prev, [key]:'' }));
  }, []);

  const updateAddr = useCallback((idx,key,val)=>{setD('prev_addresses',prev=>{const arr=prev.map((a,i)=>i===idx?{...a,[key]:val}:a);return arr;});},[setD]);
  const removeAddr = useCallback((idx)=>{setD('prev_addresses',prev=>prev.filter((_,i)=>i!==idx));},[setD]);
  const addAddr = useCallback(()=>{setD('prev_addresses',prev=>[...prev,{street:'',city:'',state:'',zip:'',start_date:'',end_date:''}]);},[setD]);

  function validate() {
    const e = {};
    if (step===0) {
      if (!d.first_name.trim()) e.first_name='Required';
      if (!d.last_name.trim())  e.last_name='Required';
      if (!d.dob||d.dob.length<10) e.dob='Enter full date MM/DD/YYYY';
      if (!d.no_ssn) {
        if (!d.ssn1||d.ssn1.length!==3||!d.ssn2||d.ssn2.length!==2||!d.ssn3||d.ssn3.length!==4)
          e.ssn1='Enter full 9-digit SSN';
      } else {
        if (!d.alien_reg.trim()) e.alien_reg='Required when no SSN';
      }
    }
    if (step===1) {
      if (!d.email.trim()) e.email='Required';
      if (d.phone.replace(/\D/g,'').length!==10) e.phone='Enter a valid 10-digit number';
    }
    if (step===2) {
      if (!d.home_address1.trim()) e.home_address1='Required';
      if (!d.home_no_apt&&!d.home_apt.trim()) e.home_apt='Enter apartment number or check "No apartment"';
      if (!d.home_city.trim()) e.home_city='Required';
      if (!d.home_state) e.home_state='Required';
      if (d.home_zip.length!==5) e.home_zip='5-digit ZIP required';
      if (!d.same_mailing) {
        if (!d.mailing_address1.trim()) e.mailing_address1='Required';
        if (!d.mailing_no_apt&&!d.mailing_apt.trim()) e.mailing_apt='Enter apartment number or check "No apartment"';
        if (!d.mailing_city.trim()) e.mailing_city='Required';
        if (!d.mailing_state) e.mailing_state='Required';
        if (d.mailing_zip.length!==5) e.mailing_zip='5-digit ZIP required';
      }
    }
    if (step===3) {
      if (d.other_names_yes===null) e.other_names_yes='Required';
      if (d.other_names_yes&&!d.other_names_list.trim()) e.other_names_list='List the names';
      if (d.lived_outside_ny===null) e.lived_outside_ny='Required';
      if (d.lived_outside_ny===true&&d.prev_addresses.length===0) e.lived_outside_ny='Please add at least one address';
    }
    setErrors(e);
    return Object.keys(e).length===0;
  }

  function next() { if (validate()) setStep(s=>Math.min(s+1,CBC_STEPS.length-1)); }
  function prev() { setStep(s=>Math.max(s-1,0)); }

  async function generate() {
    if (!validate()) return;
    setLoading(true); setApiError('');
    const ssn = d.no_ssn?'':`${d.ssn1}-${d.ssn2}-${d.ssn3}`;
    const apt = pfx => d[`${pfx}no_apt`]?'':d[`${pfx}apt`];
    const payload = {
      token,
      first_name:d.first_name, middle_name:d.middle_name, last_name:d.last_name,
      dob:d.dob, ssn, alien_reg_number:d.alien_reg,
      email:d.email, phone:d.phone,
      home_address1:d.home_address1, home_address2:apt('home_'),
      home_city:d.home_city, home_state:d.home_state, home_zip:d.home_zip,
      mailing_address1:d.same_mailing?d.home_address1:d.mailing_address1,
      mailing_address2:d.same_mailing?apt('home_'):apt('mailing_'),
      mailing_city:d.same_mailing?d.home_city:d.mailing_city,
      mailing_state:d.same_mailing?d.home_state:d.mailing_state,
      mailing_zip:d.same_mailing?d.home_zip:d.mailing_zip,
      other_names_yes:d.other_names_yes===true, other_names_no:d.other_names_yes!==true,
      other_names_list:d.other_names_list,
      lived_outside_ny_yes:d.lived_outside_ny===true, lived_outside_ny_no:d.lived_outside_ny!==true,
      prev_addresses:d.prev_addresses,
    };
    try {
      const res = await fetch(`${API_URL}/api/onboarding/submit`, {
        method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(()=>({}));
        throw new Error(body.detail || `Error: ${res.status}`);
      }
      await res.json();
      setDone(true);
    } catch(e) {
      setApiError(e.message||'Something went wrong.');
    } finally { setLoading(false); }
  }

  // Step components
  function StepPersonal() {
    return <>
      <div style={S.sec}>Personal information</div>
      <div style={S.row2}>
        <Fld label="First name" error={errors.first_name}>
          <Inp value={d.first_name} onChange={v=>setD('first_name',v)} error={errors.first_name} placeholder="Jane" />
        </Fld>
        <Fld label="Middle name or initial">
          <Inp value={d.middle_name} onChange={v=>setD('middle_name',v)} placeholder="Marie" />
        </Fld>
      </div>
      <Fld label="Last name" error={errors.last_name}>
        <Inp value={d.last_name} onChange={v=>setD('last_name',v)} error={errors.last_name} placeholder="Doe" />
      </Fld>
      <Fld label="Date of birth" error={errors.dob}>
        <Inp value={d.dob} onChange={v=>setD('dob',maskDOB(v))} error={errors.dob} placeholder="MM/DD/YYYY" inputMode="numeric" maxLength={10} />
      </Fld>
      <Fld label="Social Security Number" error={errors.ssn1}>
        <div style={S.ssnWrap}>
          <input style={{...S.inp(!!errors.ssn1),width:60,textAlign:'center',fontFamily:'monospace',letterSpacing:'.1em',opacity:d.no_ssn?0.4:1}}
            type="text" inputMode="numeric" maxLength={3} placeholder="•••" value={d.ssn1} disabled={d.no_ssn}
            onChange={e=>{const v=e.target.value.replace(/\D/g,'').slice(0,3);setD('ssn1',v);if(v.length===3)document.getElementById('ssn2')?.focus();}} />
          <span style={S.ssnSep}>–</span>
          <input id="ssn2" style={{...S.inp(!!errors.ssn1),width:48,textAlign:'center',fontFamily:'monospace',letterSpacing:'.1em',opacity:d.no_ssn?0.4:1}}
            type="text" inputMode="numeric" maxLength={2} placeholder="••" value={d.ssn2} disabled={d.no_ssn}
            onChange={e=>{const v=e.target.value.replace(/\D/g,'').slice(0,2);setD('ssn2',v);if(v.length===2)document.getElementById('ssn3')?.focus();}} />
          <span style={S.ssnSep}>–</span>
          <input id="ssn3" style={{...S.inp(!!errors.ssn1),width:64,textAlign:'center',fontFamily:'monospace',letterSpacing:'.1em',opacity:d.no_ssn?0.4:1}}
            type="text" inputMode="numeric" maxLength={4} placeholder="••••" value={d.ssn3} disabled={d.no_ssn}
            onChange={e=>setD('ssn3',e.target.value.replace(/\D/g,'').slice(0,4))} />
        </div>
        <label style={S.chk}>
          <input type="checkbox" checked={d.no_ssn} onChange={e=>{setD('no_ssn',e.target.checked);if(e.target.checked){setD('ssn1','');setD('ssn2','');setD('ssn3','');}else setD('alien_reg','');}} />
          I don't have a Social Security Number
        </label>
      </Fld>
      {d.no_ssn && <Fld label="Alien Registration Number" error={errors.alien_reg}>
        <Inp value={d.alien_reg} onChange={v=>setD('alien_reg',v)} error={errors.alien_reg} placeholder="A-number" />
      </Fld>}
    </>;
  }

  function StepContact() {
    return <>
      <div style={S.sec}>Contact details</div>
      <Fld label="Email address" error={errors.email}>
        <Inp value={d.email} onChange={v=>setD('email',v)} error={errors.email} placeholder="jane@example.com" type="email" />
      </Fld>
      <Fld label="Phone number" error={errors.phone}>
        <Inp value={d.phone} onChange={v=>setD('phone',maskPhone(v))} error={errors.phone} placeholder="(718) 555-0100" inputMode="numeric" maxLength={14} />
      </Fld>
    </>;
  }

  function AptBlock(aptKey, noAptKey) {
    return <Fld label="Apartment number" error={errors[aptKey]}>
      <Inp value={d[aptKey]} onChange={v=>setD(aptKey,v)} error={errors[aptKey]} placeholder="4B"
        disabled={d[noAptKey]} style={{opacity:d[noAptKey]?0.4:1}} />
      <label style={S.chk}>
        <input type="checkbox" checked={d[noAptKey]} onChange={e=>{setD(noAptKey,e.target.checked);if(e.target.checked)setD(aptKey,'');}} />
        I live in a private house (no apartment)
      </label>
    </Fld>;
  }

  function AddrBlock(prefix) {
    const k = s => prefix+s;
    return <>
      <Fld label="Street address" error={errors[k('address1')]}>
        <Inp value={d[k('address1')]} onChange={v=>setD(k('address1'),v)} error={errors[k('address1')]} placeholder="123 Main St" />
      </Fld>
      {AptBlock(k('apt'), k('no_apt'))}
      <div style={S.row3}>
        <Fld label="City" error={errors[k('city')]}><Inp value={d[k('city')]} onChange={v=>setD(k('city'),v)} error={errors[k('city')]} placeholder="Brooklyn" /></Fld>
        <Fld label="State" error={errors[k('state')]}><Sel value={d[k('state')]} onChange={v=>setD(k('state'),v)} error={errors[k('state')]} options={US_STATES} placeholder="State" /></Fld>
        <Fld label="ZIP" error={errors[k('zip')]}><Inp value={d[k('zip')]} onChange={v=>setD(k('zip'),v.replace(/\D/g,'').slice(0,5))} error={errors[k('zip')]} placeholder="11201" inputMode="numeric" maxLength={5} /></Fld>
      </div>
    </>;
  }

  function StepAddress() {
    return <>
      <div style={S.sec}>Home address</div>
      {AddrBlock('home_')}
      <div style={S.divider} />
      <label style={S.chk}><input type="checkbox" checked={d.same_mailing} onChange={e=>setD('same_mailing',e.target.checked)} /> Mailing address same as home</label>
      {!d.same_mailing && <><div style={{...S.sec,marginTop:'1.25rem'}}>Mailing address</div>{AddrBlock('mailing_')}</>}
    </>;
  }

  function YNField(label, field, err) {
    return <Fld label={label} error={err}>
      <div style={S.radioGroup}>
        <label style={S.radio}><input type="radio" name={field} checked={d[field]===true} onChange={()=>setD(field,true)} /> Yes</label>
        <label style={S.radio}><input type="radio" name={field} checked={d[field]===false} onChange={()=>setD(field,false)} /> No</label>
      </div>
    </Fld>;
  }

  function StepHistory() {
    return <>
      <div style={S.sec}>Name history</div>
      {YNField("Have you been known by any other names (maiden, aliases)?", "other_names_yes", errors.other_names_yes)}
      {d.other_names_yes===true && <Fld label="List all other names" error={errors.other_names_list}>
        <Inp value={d.other_names_list} onChange={v=>setD('other_names_list',v)} error={errors.other_names_list} placeholder="Jane Smith, Jane Lee" />
      </Fld>}
      <div style={S.divider} />
      <div style={S.sec}>Residence history</div>
      {YNField("Have you lived outside New York or in a US territory in the past 5 years?", "lived_outside_ny", errors.lived_outside_ny)}
      {d.lived_outside_ny===true && <div style={{marginTop:'.75rem'}}>
        {d.prev_addresses.map((a,i)=>(
          <div key={i} style={S.subCard}>
            <div style={S.subHdr}><span style={{fontSize:12,fontWeight:500,color:'#6b7280'}}>Address {i+1}</span>
              <button style={S.rmBtn} onClick={()=>removeAddr(i)}>Remove</button></div>
            <Fld label="Street address"><input style={S.inp(false)} value={a.street} placeholder="456 Oak Ave" onChange={e=>updateAddr(i,'street',e.target.value)} /></Fld>
            <div style={S.row2}>
              <Fld label="City"><input style={S.inp(false)} value={a.city} placeholder="Newark" onChange={e=>updateAddr(i,'city',e.target.value)} /></Fld>
              <Fld label="State"><select style={S.sel(false)} value={a.state} onChange={e=>updateAddr(i,'state',e.target.value)}>
                <option value="">–</option>{US_STATES.map(s=><option key={s} value={s}>{s}</option>)}</select></Fld>
            </div>
            <div style={S.row3}>
              <Fld label="ZIP"><input style={S.inp(false)} value={a.zip} placeholder="07101" maxLength={5} onChange={e=>updateAddr(i,'zip',e.target.value.replace(/\D/g,'').slice(0,5))} /></Fld>
              <Fld label="Start date"><input style={S.inp(false)} value={a.start_date} placeholder="MM/DD/YYYY" maxLength={10} inputMode="numeric" onChange={e=>updateAddr(i,'start_date',maskDOB(e.target.value))} /></Fld>
              <Fld label="End date"><input style={S.inp(false)} value={a.end_date} placeholder="MM/DD/YYYY" maxLength={10} inputMode="numeric" onChange={e=>updateAddr(i,'end_date',maskDOB(e.target.value))} /></Fld>
            </div>
          </div>
        ))}
        {d.prev_addresses.length<5 && <button style={S.addBtn} onClick={addAddr}>+ Add address</button>}
      </div>}
    </>;
  }

  if (done) return <DocDone title="CBC Information Worksheet" taskId={task.id} token={token} onDone={()=>onComplete(task.id)} />;

  const stepComponents = [StepPersonal, StepContact, StepAddress, StepHistory];
  const StepComp = stepComponents[step];
  const isLast = step === CBC_STEPS.length - 1;

  return (
    <div style={S.page}>
      <button style={{ ...S.btnBack, marginBottom:'1.25rem' }} onClick={onBack}>← Back</button>
      <div style={{ marginBottom:'1.25rem' }}>
        <div style={S.h1}>CBC Information Worksheet</div>
        <div style={S.sub}>Step {step+1} of {CBC_STEPS.length} — {CBC_STEPS[step]}</div>
      </div>
      <div style={S.card}>
        {StepComp()}
        {Object.values(errors).some(Boolean) && <div style={{...S.err,marginTop:'.75rem'}}>Please complete the highlighted fields above.</div>}
        {apiError && <div style={{...S.err,marginTop:'.75rem'}}>{apiError}</div>}
      </div>
      <div style={S.actions}>
        <button style={{...S.btnBack,opacity:step===0?0.4:1,cursor:step===0?'default':'pointer'}} onClick={prev} disabled={step===0}>← Back</button>
        {isLast
          ? <button style={S.btnGen} onClick={generate} disabled={loading}>
              {loading && <span style={S.spinner} />}
              {loading?'Submitting…':'Submit to HR'}
            </button>
          : <button style={S.btnNext} onClick={next}>Continue →</button>}
      </div>
    </div>
  );
}

// ── Task: W-4 ─────────────────────────────────────────────────────────────────

const W4_STEPS = ['Personal','Tax Info','Review'];

function W4Task({ task, token, session, onBack, onComplete }) {
  const [step, setStep] = useState(0);
  const [d, setDState] = useState({
    first_name: session.first_name||'', middle_initial:'', last_name: session.last_name||'',
    ssn:'', address:'', city_state_zip:'',
    filing_status:'single',
    step2_checkbox:false,
    step3_children:'', step3_other:'', step3_total:'',
    step4a_other_income:'', step4b_deductions:'', step4c_extra:'',
    exempt:false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [done, setDone] = useState(false);

  const setD = useCallback((key,val)=>{
    setDState(prev=>({...prev,[key]:typeof val==='function'?val(prev[key]):val}));
    setErrors(prev=>({...prev,[key]:''}));
  },[]);

  function validate() {
    const e = {};
    if (step===0) {
      if (!d.first_name.trim()) e.first_name='Required';
      if (!d.last_name.trim())  e.last_name='Required';
      if (!d.ssn.replace(/\D/g,'') || d.ssn.replace(/\D/g,'').length!==9) e.ssn='Enter full 9-digit SSN';
      if (!d.address.trim()) e.address='Required';
      if (!d.city_state_zip.trim()) e.city_state_zip='Required';
    }
    setErrors(e);
    return Object.keys(e).length===0;
  }

  function next() { if(validate()) setStep(s=>Math.min(s+1,W4_STEPS.length-1)); }
  function prev() { setStep(s=>Math.max(s-1,0)); }

  function maskSSN(v) {
    v = v.replace(/\D/g,'').slice(0,9);
    if (v.length > 3) v = v.slice(0,3)+'-'+v.slice(3);
    if (v.length > 6) v = v.slice(0,6)+'-'+v.slice(6);
    return v;
  }

  async function generate() {
    if (!validate()) return;
    setLoading(true); setApiError('');
    const payload = { token, ...d };
    try {
      const res = await fetch(`${API_URL}/api/onboarding/submit-w4`, {
        method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(()=>({}));
        throw new Error(body.detail || `Error: ${res.status}`);
      }
      await res.json();
      setDone(true);
    } catch(e) {
      setApiError(e.message||'Something went wrong.');
    } finally { setLoading(false); }
  }

  function StepPersonal() {
    return <>
      <div style={S.sec}>Personal information</div>
      <div style={S.row2}>
        <Fld label="First name" error={errors.first_name}><Inp value={d.first_name} onChange={v=>setD('first_name',v)} error={errors.first_name} placeholder="Jane" /></Fld>
        <Fld label="Middle initial"><Inp value={d.middle_initial} onChange={v=>setD('middle_initial',v.slice(0,1))} placeholder="M" maxLength={1} /></Fld>
      </div>
      <Fld label="Last name" error={errors.last_name}><Inp value={d.last_name} onChange={v=>setD('last_name',v)} error={errors.last_name} placeholder="Doe" /></Fld>
      <Fld label="Social Security Number" error={errors.ssn}>
        <Inp value={d.ssn} onChange={v=>setD('ssn',maskSSN(v))} error={errors.ssn} placeholder="XXX-XX-XXXX" inputMode="numeric" maxLength={11} />
      </Fld>
      <Fld label="Home address" error={errors.address}><Inp value={d.address} onChange={v=>setD('address',v)} error={errors.address} placeholder="123 Main St Apt 4B" /></Fld>
      <Fld label="City, State, ZIP" error={errors.city_state_zip}><Inp value={d.city_state_zip} onChange={v=>setD('city_state_zip',v)} error={errors.city_state_zip} placeholder="Brooklyn, NY 11201" /></Fld>
      <Fld label="Filing status (Step 1c)">
        <div style={S.radioGroup}>
          <label style={S.radio}><input type="radio" name="filing" checked={d.filing_status==='single'} onChange={()=>setD('filing_status','single')} /> Single or Married filing separately</label>
          <label style={S.radio}><input type="radio" name="filing" checked={d.filing_status==='mfj'} onChange={()=>setD('filing_status','mfj')} /> Married filing jointly or Qualifying surviving spouse</label>
          <label style={S.radio}><input type="radio" name="filing" checked={d.filing_status==='hoh'} onChange={()=>setD('filing_status','hoh')} /> Head of household</label>
        </div>
      </Fld>
    </>;
  }

  function StepTaxInfo() {
    return <>
      <div style={S.sec}>Step 2 — Multiple jobs</div>
      <label style={S.chk}>
        <input type="checkbox" checked={d.step2_checkbox} onChange={e=>setD('step2_checkbox',e.target.checked)} />
        Check this box if there are only two jobs total (Step 2c)
      </label>
      <div style={{...S.divider,margin:'1.25rem 0'}} />
      <div style={S.sec}>Step 3 — Dependents (optional)</div>
      <div style={S.row2}>
        <Fld label="3(a) Qualifying children × $2,200"><Inp value={d.step3_children} onChange={v=>setD('step3_children',v)} placeholder="$0.00" /></Fld>
        <Fld label="3(b) Other dependents × $500"><Inp value={d.step3_other} onChange={v=>setD('step3_other',v)} placeholder="$0.00" /></Fld>
      </div>
      <Fld label="Step 3 total"><Inp value={d.step3_total} onChange={v=>setD('step3_total',v)} placeholder="$0.00" /></Fld>
      <div style={{...S.divider,margin:'1.25rem 0'}} />
      <div style={S.sec}>Step 4 — Other adjustments (optional)</div>
      <Fld label="4(a) Other income (not from jobs)"><Inp value={d.step4a_other_income} onChange={v=>setD('step4a_other_income',v)} placeholder="$0.00" /></Fld>
      <Fld label="4(b) Deductions"><Inp value={d.step4b_deductions} onChange={v=>setD('step4b_deductions',v)} placeholder="$0.00" /></Fld>
      <Fld label="4(c) Extra withholding per pay period"><Inp value={d.step4c_extra} onChange={v=>setD('step4c_extra',v)} placeholder="$0.00" /></Fld>
      <div style={{...S.divider,margin:'1.25rem 0'}} />
      <label style={S.chk}>
        <input type="checkbox" checked={d.exempt} onChange={e=>setD('exempt',e.target.checked)} />
        I claim exemption from withholding for 2026
      </label>
    </>;
  }

  function StepReview() {
    return <>
      <div style={S.sec}>Review before generating</div>
      {[
        ['First name', d.first_name||'—'], ['Last name', d.last_name||'—'],
        ['SSN', d.ssn||'—'], ['Address', d.address||'—'],
        ['City/State/ZIP', d.city_state_zip||'—'],
        ['Filing status', d.filing_status==='single'?'Single/MFS':d.filing_status==='mfj'?'MFJ':'Head of Household'],
        ['Multiple jobs (2c)', d.step2_checkbox?'Yes':'No'],
        ['Exempt from withholding', d.exempt?'Yes':'No'],
      ].map(([k,v])=>(
        <div key={k} style={{ display:'flex', borderBottom:'1px solid #f3f4f6', padding:'8px 0' }}>
          <div style={{ fontSize:13, color:'#9ca3af', width:'45%' }}>{k}</div>
          <div style={{ fontSize:13, color:'#1a1a1a', fontWeight:500 }}>{v}</div>
        </div>
      ))}
      <div style={{ fontSize:12, color:'#6b7280', marginTop:'1rem', background:'#f9fafb', borderRadius:8, padding:'.75rem' }}>
        ℹ️ Your employer information will be automatically filled in from your offer details.
      </div>
    </>;
  }

  if (done) return <DocDone title="W-4 Withholding Certificate" taskId={task.id} token={token} onDone={()=>onComplete(task.id)} />;

  const stepComponents = [StepPersonal, StepTaxInfo, StepReview];
  const StepComp = stepComponents[step];
  const isLast = step === W4_STEPS.length-1;

  return (
    <div style={S.page}>
      <button style={{...S.btnBack,marginBottom:'1.25rem'}} onClick={onBack}>← Back</button>
      <div style={{marginBottom:'1.25rem'}}>
        <div style={S.h1}>W-4 Withholding Certificate</div>
        <div style={S.sub}>Step {step+1} of {W4_STEPS.length} — {W4_STEPS[step]}</div>
      </div>
      <div style={S.card}>
        {StepComp()}
        {Object.values(errors).some(Boolean) && <div style={{...S.err,marginTop:'.75rem'}}>Please complete the highlighted fields above.</div>}
        {apiError && <div style={{...S.err,marginTop:'.75rem'}}>{apiError}</div>}
      </div>
      <div style={S.actions}>
        <button style={{...S.btnBack,opacity:step===0?0.4:1,cursor:step===0?'default':'pointer'}} onClick={prev} disabled={step===0}>← Back</button>
        {isLast
          ? <button style={S.btnGen} onClick={generate} disabled={loading}>
              {loading&&<span style={S.spinner}/>}
              {loading?'Submitting…':'Submit to HR'}
            </button>
          : <button style={S.btnNext} onClick={next}>Continue →</button>}
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const [screen, setScreen] = useState('email'); // email | loading | dashboard | task
  const [token, setToken] = useState(null);
  const [session, setSession] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [loadError, setLoadError] = useState('');

  // Check for token in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (t) {
      setScreen('loading');
      verifyAndLoad(t);
    }
  }, []);

  async function verifyAndLoad(t) {
    try {
      // Verify token
      const vRes = await fetch(`${API_URL}/api/onboarding/verify?token=${t}`);
      if (!vRes.ok) throw new Error('Invalid or expired link. Please request a new one.');
      const vData = await vRes.json();

      // Load session
      const sRes = await fetch(`${API_URL}/api/onboarding/session?token=${t}`);
      if (!sRes.ok) throw new Error('Failed to load session.');
      const sData = await sRes.json();

      setToken(t);
      setSession(sData);
      setScreen('dashboard');
      // Clean URL
      window.history.replaceState({}, '', '/');
    } catch(e) {
      setLoadError(e.message);
      setScreen('error');
    }
  }

  async function handleTaskComplete(taskId) {
    // Mark complete via API
    try {
      await fetch(`${API_URL}/api/onboarding/complete-task`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ token, task_id: taskId }),
      });
    } catch(e) { console.warn('complete-task failed', e); }

    // Refresh session
    try {
      const res = await fetch(`${API_URL}/api/onboarding/session?token=${token}`);
      if (res.ok) setSession(await res.json());
    } catch(e) {}

    setActiveTask(null);
    setScreen('dashboard');
  }

  function handleSelectTask(task) {
    setActiveTask(task);
    setScreen('task');
  }

  if (screen === 'email') return (
    <div style={S.body}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}*{box-sizing:border-box}input:focus,select:focus{outline:2px solid #2563eb;outline-offset:1px}`}</style>
      <EmailScreen onSent={() => {}} />
    </div>
  );

  if (screen === 'loading') return (
    <div style={{...S.body,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{textAlign:'center'}}>
        <div style={{width:32,height:32,border:'3px solid #e5e7eb',borderTopColor:'#2563eb',borderRadius:'50%',animation:'spin .7s linear infinite',margin:'0 auto 12px'}} />
        <div style={{fontSize:14,color:'#6b7280'}}>Signing you in…</div>
      </div>
    </div>
  );

  if (screen === 'error') return (
    <div style={S.body}>
      <div style={S.page}>
        <div style={{...S.card,textAlign:'center',marginTop:'3rem'}}>
          <div style={{fontSize:32,marginBottom:12}}>⚠️</div>
          <div style={{fontSize:15,fontWeight:500,color:'#1a1a1a',marginBottom:8}}>Link expired</div>
          <div style={{fontSize:13,color:'#6b7280',marginBottom:'1.5rem'}}>{loadError}</div>
          <button style={S.btnPrimary} onClick={()=>setScreen('email')}>Request a new link</button>
        </div>
      </div>
    </div>
  );

  if (screen === 'dashboard') return (
    <div style={S.body}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}*{box-sizing:border-box}input:focus,select:focus{outline:2px solid #2563eb;outline-offset:1px}`}</style>
      <Dashboard session={session} token={token} onSelectTask={handleSelectTask} />
    </div>
  );

  if (screen === 'task' && activeTask) {
    const taskProps = {
      task: activeTask, token, session,
      onBack: () => setScreen('dashboard'),
      onComplete: handleTaskComplete,
    };
    let TaskComp = null;
    if (activeTask.id === 'cbc_worksheet') TaskComp = CBCTask;
    else if (activeTask.id === 'w4') TaskComp = W4Task;
    else if (activeTask.id === 'scr') TaskComp = ScrTask;
    else if (activeTask.type === 'upload') TaskComp = UploadTask;

    if (!TaskComp) return (
      <div style={S.body}>
        <div style={S.page}>
          <button style={{...S.btnBack,marginBottom:'1.25rem'}} onClick={()=>setScreen('dashboard')}>← Back</button>
          <div style={S.card}>
            <div style={{textAlign:'center',padding:'2rem 0'}}>
              <div style={{fontSize:32,marginBottom:12}}>🚧</div>
              <div style={{fontSize:15,fontWeight:500}}>Coming soon</div>
              <div style={{fontSize:13,color:'#6b7280',marginTop:8}}>{activeTask.title} will be available shortly.</div>
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <div style={S.body}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}*{box-sizing:border-box}input:focus,select:focus{outline:2px solid #2563eb;outline-offset:1px}`}</style>
        <TaskComp {...taskProps} />
      </div>
    );
  }

  return null;
}